import React from 'react';
import Icon from '../../../components/AppIcon';

const CompetitiveDifferentiation = () => {
  const differentiators = [
    {
      title: '90-Day Launch Guarantee',
      bootminds: 'Operational in 90 days with financial guarantee',
      others: '18-24 months typical timeline with no guarantees',
      icon: 'Zap',
      color: 'text-blue-600'
    },
    {
      title: 'Risk-Free Pricing',
      bootminds: 'Fixed-rate options + performance guarantees',
      others: 'Hourly billing with cost overruns typical',
      icon: 'Shield',
      color: 'text-green-600'
    },
    {
      title: 'Compliance Guarantee',
      bootminds: 'Milestone-based compliance commitments backed by remediation guarantees',
      others: 'Best efforts only, client bears all risk',
      icon: 'Scale',
      color: 'text-purple-600'
    },
    {
      title: 'Talent Assurance',
      bootminds: '5 leaders + 100 CVs in 8 weeks or work free',
      others: '6-12 months with no delivery commitments',
      icon: 'Users',
      color: 'text-orange-600'
    },
    {
      title: 'Exit Strategy',
      bootminds: 'Multiple exit options built-in from day one',
      others: 'Complex unwinding with penalties',
      icon: 'GitBranch',
      color: 'text-red-600'
    },
    {
      title: 'Executive Focus',
      bootminds: 'Board-ready materials in 10 days',
      others: 'Tactical focus without strategic alignment',
      icon: 'Target',
      color: 'text-indigo-600'
    }
  ];

  const provenOutcomes = [
    {
      metric: '1,000+',
      label: 'Professionals Placed',
      description: "Across our team's GCC engagements since 2018",
      icon: 'Users'
    },
    {
      metric: '$20M+',
      label: 'Client Savings Delivered',
      description: 'During the 2018–2024 Intelliswift alliance',
      icon: 'TrendingDown'
    },
    {
      metric: '50–60%',
      label: 'Cost Savings vs US',
      description: 'Zinnov GCC Cost Index 2024',
      icon: 'BarChart3'
    },
    {
      metric: '90 days',
      label: 'To Operational Launch',
      description: 'Parallel-track Launch Blueprint methodology',
      icon: 'Zap'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-background via-muted/20 to-background">
      <div className="container-executive">
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Award" size={16} />
            <span>Why Industry Leaders Choose Bootminds</span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-headline font-bold text-text-primary mb-6">
            Not Just Faster.
            <span className="block text-accent">Fundamentally Different.</span>
          </h2>

          <p className="text-xl text-text-secondary">
            We don't just consult. We guarantee outcomes, share risk, and align our success with yours.
          </p>
        </div>

        {/* Differentiators Grid */}
        <div className="space-y-4 mb-16">
          {differentiators.map((diff, index) => (
            <div key={index} className="card-elevated rounded-xl p-6 hover:shadow-executive-hover transition-all duration-300">
              <div className="grid md:grid-cols-3 gap-6 items-center">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={diff.icon} size={24} color="white" />
                  </div>
                  <h3 className="font-headline font-bold text-text-primary text-lg">
                    {diff.title}
                  </h3>
                </div>

                <div className="bg-success/10 rounded-lg p-4">
                  <div className="text-xs font-semibold text-success mb-1 flex items-center space-x-1">
                    <Icon name="CheckCircle" size={14} />
                    <span>BOOTMINDS</span>
                  </div>
                  <p className="text-sm text-text-primary font-medium">{diff.bootminds}</p>
                </div>

                <div className="bg-muted rounded-lg p-4">
                  <div className="text-xs font-semibold text-text-secondary mb-1 flex items-center space-x-1">
                    <Icon name="X" size={14} />
                    <span>TRADITIONAL APPROACH</span>
                  </div>
                  <p className="text-sm text-text-secondary">{diff.others}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Proven Outcomes */}
        <div className="card-elevated rounded-2xl p-8 lg:p-12 bg-gradient-to-br from-primary/5 to-accent/5">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-headline font-bold text-text-primary mb-4">
              Proven Track Record
            </h3>
            <p className="text-lg text-text-secondary">
              Numbers that speak louder than promises
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {provenOutcomes.map((outcome, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon name={outcome.icon} size={32} color="white" />
                </div>
                <div className="text-4xl font-bold text-primary mb-2">{outcome.metric}</div>
                <div className="font-semibold text-text-primary mb-1">{outcome.label}</div>
                <div className="text-sm text-text-secondary">{outcome.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Why It Matters */}
        <div className="mt-16 text-center max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8">
            <h3 className="text-2xl font-headline font-bold text-text-primary mb-4">
              The Bottom Line
            </h3>
            <p className="text-lg text-text-secondary mb-6">
              While others provide consulting services, we guarantee outcomes. While they bill hourly with no accountability, 
              we share your risk with fixed-rate options and performance guarantees. While they take 18-24 months, 
              we deliver operational GCCs in 90 days.
            </p>
            <p className="text-xl font-semibold text-text-primary">
              That's not incrementally better. That's fundamentally different.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompetitiveDifferentiation;
